// Pure navigation-resolution helper for the redesigned marketing site.
//
// Given the current feature flags, `resolveNavItems` computes the visible
// primary menu plus whether the Login and Blog entries should be shown. It is
// the single, testable source of truth for navigation visibility so enabling a
// hidden section is a configuration change only (Requirements 2.4, 12.4).
//
// Output shape:
//   {
//     primary: NavItem[],   // ordered visible Primary_Menu items
//     showLogin: boolean,   // top-right Login entry visible?
//     showBlog: boolean,    // Blog/News entry visible?
//   }
//
// A NavItem is `{ id, label, name, to }` where `id` is a stable identifier,
// `label` is the display text, `name` is the Vue Router route name, and `to`
// is the route path.

// Core items always present, in order (Requirement 1.2).
const HOME = { id: 'home', label: 'Home', name: 'home', to: '/' };
const ABOUT = { id: 'about', label: 'About Us', name: 'about', to: '/about' };
const SERVICES = { id: 'services', label: 'Services', name: 'services', to: '/services' };
const PROJECTS = { id: 'projects', label: 'Projects', name: 'projects', to: '/projects' };
const CONTACT = { id: 'contact', label: 'Contact Us', name: 'contact', to: '/contact' };

// Software is inserted between Projects and Contact Us when its flag is on
// (Requirements 2.3, 3 of the IA). It is never linked while the flag is off.
const SOFTWARE = { id: 'software', label: 'Software', name: 'software', to: '/software' };

/**
 * Compute the visible navigation from the feature flags.
 *
 * @param {{ software?: boolean, login?: boolean, blog?: boolean }} [features]
 * @returns {{ primary: Array<{id: string, label: string, name: string, to: string}>, showLogin: boolean, showBlog: boolean }}
 */
export function resolveNavItems(features) {
  const software = Boolean(features && features.software);
  const login = Boolean(features && features.login);
  const blog = Boolean(features && features.blog);

  // Build the primary menu in the required order, inserting Software between
  // Projects and Contact Us only when its flag is enabled.
  const primary = [HOME, ABOUT, SERVICES, PROJECTS];
  if (software) {
    primary.push(SOFTWARE);
  }
  primary.push(CONTACT);

  return {
    primary,
    showLogin: login,
    showBlog: blog,
  };
}

export default resolveNavItems;
